import React, { useEffect, useLayoutEffect, useRef } from 'react';
import Editor from '@toast-ui/editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import '@toast-ui/editor/dist/i18n/ar';
import '@toast-ui/editor/dist/i18n/zh-cn';
import '@toast-ui/editor/dist/i18n/zh-tw';
import {
  getEditorHistoryLabels,
  getEditorLanguage,
  registerEditorLocales,
} from '../utils/editorLocale';
import {
  getReportLocaleDirection,
  normalizeReportLocale,
} from '../utils/locale';

registerEditorLocales(Editor);

const BASE_TOOLBAR_ITEMS = [
  ['heading', 'bold', 'italic', 'strike'],
  ['hr', 'quote'],
  ['ul', 'ol'],
  ['table', 'link']
];

const normalizeMarkdown = (value) => (typeof value === 'string' ? value : '');

const normalizeHeight = (height) => (
  typeof height === 'number' ? `${height}px` : (height || '400px')
);

const dispatchHistoryShortcut = (editor, redo = false) => {
  if (!editor) return;

  editor.focus();
  const editorElements = editor.getEditorElements();
  const editable = editorElements?.wwEditor?.querySelector('[contenteditable="true"], .ProseMirror');
  if (!editable) return;

  const isApplePlatform = /Mac|iPhone|iPad|iPod/i.test(window.navigator.platform || '');
  editable.dispatchEvent(new KeyboardEvent('keydown', {
    key: 'z',
    code: 'KeyZ',
    bubbles: true,
    cancelable: true,
    ctrlKey: !isApplePlatform,
    metaKey: isApplePlatform,
    shiftKey: redo
  }));
};

const createHistoryButton = ({ name, label, symbol, onClick }) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `report-rich-editor__history-button report-rich-editor__history-button--${name}`;
  button.setAttribute('aria-label', label);
  button.setAttribute('title', label);
  button.textContent = symbol;
  button.addEventListener('click', onClick);

  return {
    name: `report-${name}`,
    tooltip: label,
    el: button
  };
};

const applyAccessibilityState = (container, disabled, ariaLabel, language) => {
  if (!container) return;

  const reportLocale = normalizeReportLocale(language);
  container.classList.toggle('report-rich-editor--disabled', disabled);
  container.setAttribute('aria-disabled', String(disabled));
  container.setAttribute('lang', reportLocale);
  container.setAttribute('dir', getReportLocaleDirection(reportLocale));

  const editable = container.querySelector('.toastui-editor-ww-container .ProseMirror');
  if (editable) {
    editable.setAttribute('role', 'textbox');
    editable.setAttribute('aria-multiline', 'true');
    editable.setAttribute('aria-label', ariaLabel);
    editable.setAttribute('contenteditable', disabled ? 'false' : 'true');
    editable.setAttribute('lang', reportLocale);
    editable.setAttribute('dir', getReportLocaleDirection(reportLocale));
  }
};

const MarkdownRichEditor = ({
  value = '',
  onChange,
  disabled = false,
  height = '400px',
  ariaLabel = 'Rich text editor',
  language = 'en-US'
}) => {
  const containerRef = useRef(null);
  const editorRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(normalizeMarkdown(value));
  const heightRef = useRef(height);
  const disabledRef = useRef(disabled);
  const ariaLabelRef = useRef(ariaLabel);
  const syncingRef = useRef(false);
  const suppressedMarkdownRef = useRef(null);
  const disposedRef = useRef(false);

  heightRef.current = height;
  disabledRef.current = disabled;
  ariaLabelRef.current = ariaLabel;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useLayoutEffect(() => {
    if (!containerRef.current) return undefined;

    disposedRef.current = false;

    const reportLocale = normalizeReportLocale(language);
    const historyLabels = getEditorHistoryLabels(reportLocale);
    const undoButton = createHistoryButton({
      name: 'undo',
      label: historyLabels.undo,
      symbol: '↶',
      onClick: () => dispatchHistoryShortcut(editorRef.current, false)
    });
    const redoButton = createHistoryButton({
      name: 'redo',
      label: historyLabels.redo,
      symbol: '↷',
      onClick: () => dispatchHistoryShortcut(editorRef.current, true)
    });

    const editor = new Editor({
      el: containerRef.current,
      height: normalizeHeight(heightRef.current),
      minHeight: normalizeHeight(heightRef.current),
      initialValue: valueRef.current,
      initialEditType: 'wysiwyg',
      previewStyle: 'vertical',
      hideModeSwitch: true,
      usageStatistics: false,
      autofocus: false,
      language: getEditorLanguage(reportLocale),
      theme: 'dark',
      linkAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer'
      },
      toolbarItems: [
        ...BASE_TOOLBAR_ITEMS,
        [undoButton, redoButton]
      ]
    });

    editorRef.current = editor;
    applyAccessibilityState(containerRef.current, disabledRef.current, ariaLabelRef.current, reportLocale);

    const handleChange = () => {
      if (disposedRef.current) return;

      const nextMarkdown = editor.getMarkdown();
      if (syncingRef.current || nextMarkdown === suppressedMarkdownRef.current) {
        suppressedMarkdownRef.current = null;
        return;
      }
      if (nextMarkdown === valueRef.current) return;

      valueRef.current = nextMarkdown;
      onChangeRef.current?.(nextMarkdown);
    };

    editor.on('change', handleChange);

    return () => {
      disposedRef.current = true;
      editor.off('change', handleChange);
      editorRef.current = null;
      editor.destroy();
    };
  }, [language]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const nextMarkdown = normalizeMarkdown(value);
    valueRef.current = nextMarkdown;
    if (editor.getMarkdown() === nextMarkdown) return;

    syncingRef.current = true;
    try {
      editor.setMarkdown(nextMarkdown, false);
      suppressedMarkdownRef.current = editor.getMarkdown();
    } finally {
      syncingRef.current = false;
    }
  }, [value]);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      editor.setHeight(normalizeHeight(height));
      editor.setMinHeight(normalizeHeight(height));
    }
  }, [height]);

  useEffect(() => {
    applyAccessibilityState(containerRef.current, disabled, ariaLabel, language);
  }, [ariaLabel, disabled, language]);

  return (
    <div
      ref={containerRef}
      className="report-rich-editor"
      role="group"
      aria-label={ariaLabel}
      data-testid="report-rich-editor"
      lang={normalizeReportLocale(language)}
      dir={getReportLocaleDirection(language)}
    />
  );
};

export default MarkdownRichEditor;
